import { useMemo, useState } from 'react';
import { CheckIcon } from '@/client/components/icons';
import { Badge, Button, Field, Select, Sheet, Textarea, Toggle } from '@/client/components/ui';
import { mailtoLink, rideMessage, smsLink } from '@/client/messages';
import { usePatchAssignments } from '@/client/store';
import { CreditLedger, rankCandidates } from '@/domain/credit';
import { dayTypeOf, formatDayMonth, formatShort } from '@/domain/dates';
import type { AssignmentPatch } from '@/shared/api';
import {
  type Assignment,
  LEGS,
  type Legs,
  OUTCOMES,
  type Outcome,
  type Workspace,
} from '@/shared/types';

/** Shared assignment UI: the row, and the editor sheet behind it. */

export function volunteerName(workspace: Workspace, id: string | null): string | null {
  if (!id) return null;
  return workspace.volunteers.find((v) => v.id === id)?.name ?? id;
}

export function statusOf(assignment: Assignment): {
  label: string;
  tone: 'neutral' | 'brand' | 'positive' | 'caution' | 'critical';
} {
  if (assignment.outcome === 'Completed') return { label: 'Completed', tone: 'positive' };
  if (assignment.outcome === 'Volunteer Bailed') return { label: 'Bailed', tone: 'critical' };
  if (assignment.outcome === 'Rider Canceled') return { label: 'Rider canceled', tone: 'caution' };
  if (assignment.outcome === 'No Meeting') return { label: 'No meeting', tone: 'neutral' };
  if (assignment.confirmed) return { label: 'Confirmed', tone: 'positive' };
  if (assignment.volunteerId) return { label: 'Not confirmed', tone: 'caution' };
  return { label: 'Unassigned', tone: 'critical' };
}

export function AssignmentRow({
  assignment,
  workspace,
  onOpen,
}: {
  assignment: Assignment;
  workspace: Workspace;
  onOpen: (assignment: Assignment) => void;
}) {
  const name = volunteerName(workspace, assignment.volunteerId);
  const status = statusOf(assignment);
  const dayType = dayTypeOf(assignment.date, workspace.settings.meetingDays);

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(assignment)}
        className="card tap-target flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
      >
        <span className="flex w-14 shrink-0 flex-col">
          <span className="text-xs font-medium text-ink-faint">
            {dayType ? dayType.slice(0, 3) : formatShort(assignment.date).slice(0, 3)}
          </span>
          {/* Month name rather than digits: "16/08" reads as a different day
              depending on where you are. */}
          <span className="text-sm font-semibold">{formatDayMonth(assignment.date)}</span>
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">
            {name ?? <span className="text-ink-faint">Nobody assigned</span>}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <Badge tone={status.tone}>{status.label}</Badge>
            {assignment.legs !== 'Both' ? <Badge>{assignment.legs}</Badge> : null}
            {assignment.manual ? <Badge tone="brand">Manual</Badge> : null}
          </span>
        </span>

        {assignment.confirmed ? <CheckIcon className="size-5 shrink-0 text-positive" /> : null}
      </button>
    </li>
  );
}

interface RankedOption {
  id: string;
  name: string;
  credit: number;
  eligible: boolean;
  reason?: string;
}

/**
 * Volunteer options for one date, ranked by the same math the generator uses,
 * so the coordinator picking a last-minute replacement sees exactly what the
 * tool would have suggested (spec 4.3).
 */
function useRankedOptions(workspace: Workspace, assignment: Assignment): RankedOption[] {
  return useMemo(() => {
    const dayType = dayTypeOf(assignment.date, workspace.settings.meetingDays);
    const ledger = CreditLedger.from(
      workspace.volunteers,
      // Exclude this row so a volunteer is not penalised for already holding it.
      workspace.assignments.filter(
        (a) => !(a.date === assignment.date && a.riderId === assignment.riderId),
      ),
      workspace.settings.rollingWindowWeeks,
    );

    const eligible = dayType
      ? rankCandidates(workspace.volunteers, ledger, assignment.date, dayType).map((c) => ({
          id: c.volunteer.id,
          name: c.volunteer.name,
          credit: c.credit,
          eligible: true,
        }))
      : [];

    const eligibleIds = new Set(eligible.map((o) => o.id));

    // Everyone else stays selectable but is clearly marked: the coordinator
    // always has the final say, including over the tool's own rules.
    const rest = workspace.volunteers
      .filter((v) => !eligibleIds.has(v.id) && v.status !== 'Removed')
      .map((v) => ({
        id: v.id,
        name: v.name,
        credit: ledger.creditAt(v.id, assignment.date),
        eligible: false,
        reason:
          v.status === 'Paused'
            ? 'paused'
            : dayType && !(dayType === 'Thursday' ? v.availableThursday : v.availableSunday)
              ? `not available ${dayType}s`
              : 'unavailable',
      }));

    return [...eligible, ...rest];
  }, [workspace, assignment.date, assignment.riderId]);
}

export function AssignmentEditor({
  assignment,
  workspace,
  onClose,
}: {
  assignment: Assignment | null;
  workspace: Workspace;
  onClose: () => void;
}) {
  const patch = usePatchAssignments();
  const [draft, setDraft] = useState<Assignment | null>(assignment);

  // Re-seed the draft whenever a different row is opened.
  const activeKey = assignment ? `${assignment.date}|${assignment.riderId}` : null;
  const draftKey = draft ? `${draft.date}|${draft.riderId}` : null;
  if (activeKey !== draftKey) setDraft(assignment);

  const options = useRankedOptions(workspace, assignment ?? blankFor(workspace));
  if (!assignment || !draft) return null;

  const rider = workspace.riders.find((r) => r.id === assignment.riderId);
  const canConfirm = Boolean(draft.volunteerId);

  const save = () => {
    const body: AssignmentPatch = {
      date: draft.date,
      riderId: draft.riderId,
      volunteerId: draft.volunteerId,
      confirmed: draft.confirmed,
      outcome: draft.outcome,
      legs: draft.legs,
      notes: draft.notes,
    };
    patch.mutate([body]);
    onClose();
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={formatShort(assignment.date)}
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button variant="primary" onClick={save} className="flex-1">
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {rider ? <p className="text-sm text-ink-muted">Ride for {rider.name}</p> : null}

        <Field
          label="Volunteer"
          hint="Ranked by who is due next. Anyone below the line can still be picked."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={draft.volunteerId ?? ''}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  volunteerId: event.target.value || null,
                  // Un-assigning has to drop the confirmation with it.
                  confirmed: event.target.value ? draft.confirmed : false,
                })
              }
            >
              <option value="">Nobody assigned</option>
              {options
                .filter((option) => option.eligible)
                .map((option, index) => (
                  <option key={option.id} value={option.id}>
                    {index === 0 ? '★ ' : ''}
                    {option.name} — {option.credit} in window
                  </option>
                ))}
              {options.some((option) => !option.eligible) ? (
                <optgroup label="Not normally available">
                  {options
                    .filter((option) => !option.eligible)
                    .map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name} ({option.reason})
                      </option>
                    ))}
                </optgroup>
              ) : null}
            </Select>
          )}
        </Field>

        <Toggle
          label="Confirmed"
          description={
            canConfirm
              ? 'Puts their name on the public calendar.'
              : 'Assign someone before confirming.'
          }
          disabled={!canConfirm}
          checked={draft.confirmed}
          onChange={(confirmed) => setDraft({ ...draft, confirmed })}
        />

        <Field label="Legs" hint="Use this when the household handles one direction.">
          {({ id }) => (
            <Select
              id={id}
              value={draft.legs}
              onChange={(event) => setDraft({ ...draft, legs: event.target.value as Legs })}
            >
              {LEGS.map((leg) => (
                <option key={leg} value={leg}>
                  {leg}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Outcome" hint="Record this after the meeting.">
          {({ id }) => (
            <Select
              id={id}
              value={draft.outcome}
              onChange={(event) => setDraft({ ...draft, outcome: event.target.value as Outcome })}
            >
              {OUTCOMES.map((outcome) => (
                <option key={outcome} value={outcome}>
                  {outcome}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Notes">
          {({ id }) => (
            <Textarea
              id={id}
              value={draft.notes}
              placeholder="Anything the volunteer should know"
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
            />
          )}
        </Field>

        {draft.volunteerId !== assignment.volunteerId ? (
          <p className="rounded-xl bg-brand-soft px-3 py-2 text-xs text-brand-ink">
            Changing the volunteer marks this row as a hand edit, so the next Generate run will
            leave it alone.
          </p>
        ) : null}

        <MessageLinks assignment={draft} workspace={workspace} />
      </div>
    </Sheet>
  );
}

/**
 * Prefilled messages to the two people on this ride.
 *
 * Texting is one person at a time — a multi-recipient `sms:` link means
 * different things on iOS and Android — while the email goes to both at once so
 * they can see each other and reply to the same thread.
 */
function MessageLinks({ assignment, workspace }: { assignment: Assignment; workspace: Workspace }) {
  const volunteer = workspace.volunteers.find((v) => v.id === assignment.volunteerId);
  const rider = workspace.riders.find((r) => r.id === assignment.riderId);
  if (!volunteer) return null;

  const body = rideMessage({ assignment, volunteer, rider, settings: workspace.settings });
  const subject = `Ride on ${formatShort(assignment.date)}`;

  const links = [
    { label: `Text ${volunteer.name}`, href: smsLink(volunteer.phone, body) },
    rider ? { label: `Text ${rider.name}`, href: smsLink(rider.phone, body) } : null,
    {
      label: rider?.email && volunteer.email ? 'Email both' : 'Email',
      href: mailtoLink([volunteer.email, rider?.email ?? ''], subject, body),
    },
  ].filter((link): link is { label: string; href: string } => Boolean(link?.href));

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <p className="text-sm font-medium">Let them know</p>

      {links.length === 0 ? (
        <p className="text-xs text-ink-faint">
          Add a phone number or email address to {volunteer.name}
          {rider ? ` or ${rider.name}` : ''} to send a message from here.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="tap-target inline-flex items-center rounded-xl border border-border-strong px-4 text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
          </div>
          <p className="text-xs text-ink-faint">
            Opens your own messages app with the details already written. The calendar invite goes
            to both of them on the next sync.
          </p>
        </>
      )}
    </div>
  );
}

function blankFor(workspace: Workspace): Assignment {
  return {
    date: workspace.today,
    riderId: workspace.riders[0]?.id ?? 'R1',
    volunteerId: null,
    confirmed: false,
    outcome: 'Pending',
    legs: 'Both',
    manual: false,
    publicEventId: null,
    inviteEventId: null,
    notes: '',
  };
}
